import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Components } from '../../styled';
import ProjectTableHeader from '../../../../components/ProjectTableHeader';
import useFormTextState from '../../../../hooks/useFormTextState';
import ProjectTable from '../../../../components/ProjectTable';
import ProjectSelect from '../../../../components/inputs/ProjectSelect';
import { filteringModel, tableColumns } from '../../../../components/ProjectTable/constants';
import { ActivityIndicatorContext } from '../../../../providers/ActivityIndicatorProvider/context';
import useModalState from '../../../../hooks/useModalState';
import { MentorShort } from '../../../../api/types/mentorShort';
import { Author } from '../../../../api/types/author';
import { getAllAuthors } from '../../../../api/author';
import { deleteMentorShort, getAllMentorShorts, reorderMentorShorts } from '../../../../api/mentorShort';
import CreateOrEditMentorShortModal from '../../../../components/modals/CreateOrEditMentorShortModal';
import { ToastContext } from '../../../../providers/ToastProvider/context';
import { isProjectError } from '../../../../utils/errors';
import ReorderableShortsList from './ReorderableShortsList';
import { Components as ShortsComponents } from './styled';

// Standalone short videos recorded with each mentor (e.g. the Home feed).
// Filter by mentor to manage one mentor's shorts; new shorts default to the
// filtered mentor.
const MentorShorts = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);
  const [clips, setClips] = useState<MentorShort[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [authorFilter, setAuthorFilter] = useState<string | undefined>(undefined);
  const searchProps = useFormTextState();
  const clipModalProps = useModalState<{ clip?: MentorShort }>();

  const refreshData = async () => {
    setIsLoading(true);
    const [clipResult, authorResult] = await Promise.all([getAllMentorShorts(), getAllAuthors()]);
    setIsLoading(false);
    setClips(clipResult);
    setAuthors(authorResult.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
  };

  useEffect(() => {
    refreshData().then();
  }, []);

  const authorNameById = useMemo(() => new Map(authors.map((a) => [a.id, a.name])), [authors]);
  const rows = useMemo(
    () =>
      clips
        .filter((clip) => !authorFilter || clip.authorId === authorFilter)
        .map((clip) => ({ ...clip, authorName: authorNameById.get(clip.authorId) })),
    [clips, authorFilter, authorNameById],
  );

  // Order is per mentor, so reordering needs exactly one mentor and no search narrowing the list.
  const canReorder = !!authorFilter && !searchProps.value?.trim();
  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => (a.authorName || '').localeCompare(b.authorName || '') || a.order - b.order),
    [rows],
  );

  // Dragging only changes this draft; nothing is written until the user saves.
  const [draftOrderIds, setDraftOrderIds] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    setDraftOrderIds(undefined);
  }, [authorFilter, searchProps.value]);

  const reorderItems = useMemo(() => {
    if (!draftOrderIds) {
      return orderedRows;
    }
    const rowById = new Map(orderedRows.map((row) => [row.id, row]));
    const drafted = draftOrderIds
      .map((id) => rowById.get(id))
      .filter((row): row is (typeof orderedRows)[number] => !!row);
    // Shorts added after the drag started go last, as they would when saved.
    const draftedIds = new Set(draftOrderIds);
    return [...drafted, ...orderedRows.filter((row) => !draftedIds.has(row.id))];
  }, [draftOrderIds, orderedRows]);

  const onSaveOrder = async () => {
    if (!draftOrderIds) {
      return;
    }
    setIsLoading(true);
    const orderedIds = reorderItems.map((row) => row.id);
    const result = await reorderMentorShorts(orderedIds);
    setIsLoading(false);
    const error = isProjectError(result);
    if (error) {
      showErrorToast(error);
      return;
    }
    const newOrderById = new Map(orderedIds.map((id, index) => [id, index]));
    setClips((current) => current.map((clip) => ({ ...clip, order: newOrderById.get(clip.id) ?? clip.order })));
    setDraftOrderIds(undefined);
    showSuccessToast('Order saved');
  };

  const onEdit = (id: string) => {
    const clip = clips.find((item) => item.id === id);
    if (clip) {
      clipModalProps.open({ clip });
    }
  };

  const onDelete = async (id: string) => {
    setIsLoading(true);
    await deleteMentorShort(id);
    setIsLoading(false);
    await refreshData();
  };

  const onModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      refreshData().then();
    }
    clipModalProps.onModalClose();
  };

  return (
    <Components.ComponentContainer>
      <ProjectTableHeader
        onSearchChange={searchProps.onChangeText}
        searchLabel={'Search by video title, mentor or ID'}
        searchPlaceholder={'Enter search term'}>
        <ProjectSelect
          value={authorFilter}
          onChangeSelectedItem={(e) => setAuthorFilter(e.target.value || undefined)}
          onClearValue={() => setAuthorFilter(undefined)}
          processShownLabel={(id) => authorNameById.get(id) ?? id}
          placeholder={'All mentors'}>
          {authors.map((author) => (
            <Components.MenuItem key={author.id} value={author.id}>
              {author.name}
            </Components.MenuItem>
          ))}
        </ProjectSelect>
        <Components.SearchBarSeparator />
        <Components.PrimaryButton onClick={() => clipModalProps.open({ clip: undefined })}>
          Add Video
        </Components.PrimaryButton>
      </ProjectTableHeader>
      <ShortsComponents.SaveOrderBar>
        <ShortsComponents.Hint>
          {!canReorder
            ? 'Select a mentor to reorder shorts'
            : draftOrderIds
            ? 'The order has changed. Save it to apply.'
            : 'Drag rows to reorder this mentor’s shorts, then save.'}
        </ShortsComponents.Hint>
        {canReorder && draftOrderIds && (
          <ShortsComponents.SaveOrderActions>
            <ShortsComponents.ActionButton type={'button'} onClick={() => setDraftOrderIds(undefined)}>
              Discard
            </ShortsComponents.ActionButton>
            <Components.PrimaryButton onClick={onSaveOrder}>Save order</Components.PrimaryButton>
          </ShortsComponents.SaveOrderActions>
        )}
      </ShortsComponents.SaveOrderBar>
      <Components.TableWrapper>
        {canReorder ? (
          <ReorderableShortsList items={reorderItems} onReorder={setDraftOrderIds} onEdit={onEdit} />
        ) : (
          <ProjectTable
            data={orderedRows}
            columns={tableColumns.mentorShorts({ onEdit, onDelete })}
            filteringModel={filteringModel([
              { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
            ])}
            showRowCountText={'Found videos'}
            customPageSize={15}
          />
        )}
      </Components.TableWrapper>
      <CreateOrEditMentorShortModal
        {...clipModalProps}
        onModalClose={onModalClose}
        authors={authors}
        defaultAuthorId={authorFilter}
      />
    </Components.ComponentContainer>
  );
};

export default MentorShorts;
